import { drawContract } from '@daymade/api-contracts';
import { Monthly10k } from '@daymade/common-lib/constants';
import { ClientInferResponseBody } from '@ts-rest/core';
import { createContext, useContext, useEffect, useState } from 'react';
import { drawApiClient } from 'utils/apiClient';
import HttpStatusCode from 'utils/HttpStatusCode';

type TDraw = ClientInferResponseBody<
  typeof drawContract.public.getDrawTypeCached,
  200
>['next_draw'];

const MonthlyRaffleDataContext = createContext<{
  data: Awaited<ReturnType<typeof loadData>> | null;
}>({ data: null });

async function loadData() {
  const drawRequest = await drawApiClient.public.getDrawTypeCached({
    params: { draw_code: Monthly10k.drawTypeCode },
  });

  if (drawRequest.status === HttpStatusCode.OK) {
    return drawRequest.body.next_draw;
  }
  return null;
}

/** A hook that gets the monthly raffle data, either from MonthlyRaffleContextProvider if it appears in the component
 * tree or from the API */
export const useMonthlyRaffleData = () => {
  const context = useContext(MonthlyRaffleDataContext);
  const [monthlyRaffleData, setMonthlyRaffleData] = useState<TDraw | null>(
    context.data,
  );
  useEffect(() => {
    if (!context.data) {
      (async () => {
        setMonthlyRaffleData(await loadData());
      })();
    }
  }, []);

  return {
    monthlyRaffleData,
  };
};

/** A react context provider that makes the request to get the monthly raffle data and stores in. Any children are then
 * able to access the cached copy of the monthly data. This is useful for preloading the monthly raffle data.
 */
export function MonthlyRaffleContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [data, setData] = useState<Awaited<ReturnType<typeof loadData>> | null>(
    null,
  );

  useEffect(() => {
    (async () => {
      const result = await loadData();
      setData(result);
    })();
  }, []);

  return (
    <MonthlyRaffleDataContext.Provider value={{ data }}>
      {children}
    </MonthlyRaffleDataContext.Provider>
  );
}
